// 매수 등록/수정 화면 (tb_buy_history)
// 상단: 필터 / 좌측: 목록 / 우측: 폼
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchBuyList, fetchNextTurnNo, createBuy, updateBuy, deleteBuy } from '../../api/buy';
import { fetchRealtimePrices } from '../../api/price';
import { fetchAccounts } from '../../api/accounts';
import { fetchStocks } from '../../api/stocks';
import { fetchExchangeRate } from '../../api/marketIndex';
import { subscribeTabNav } from '../../utils/tabNav';
import PageLayout from '../../components/PageLayout';
import StockChartPopup from '../../components/common/StockChartPopup';
import AlertButton from '../../components/common/AlertButton';
import AlertQuickPopup from '../../components/common/AlertQuickPopup';
import './BuyPage.css';

const PENDING_COLOR = '#e67e22'; // 매수일자 없는 행의 단가·수량·금액 색상 (종목명 클릭색과 동일)

const EMPTY_FORM = {
  account_name:       '',
  ticker:             '',
  buy_date:           '',
  buy_price:          '',
  quantity:           '',
  target_profit_rate: '',
  memo:               '',
  exchange_rate:      '',
};

const fNum = (v) =>
  v != null && v !== '' ? Number(v).toLocaleString('ko-KR') : '';

const fQty = (v) =>
  v != null && v !== ''
    ? Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 })
    : '';

const sign = (v) => v > 0 ? '+' : '';
const fDiff = (prevDiff, diffRate) => {
  if (prevDiff == null && diffRate == null) return '';
  const d = prevDiff != null ? `${sign(prevDiff)}${Math.round(prevDiff).toLocaleString('ko-KR')}` : '';
  const r = diffRate != null ? `${sign(diffRate)}${Number(diffRate).toFixed(2)}%` : '';
  if (d && r) return `${d} (${r})`;
  return d || r;
};
const diffColor = (v) => v == null ? undefined : v > 0 ? '#d32f2f' : v < 0 ? '#1565c0' : undefined;

const BuyPage = ({ isActive }) => {
  const [buyList,        setBuyList]        = useState([]);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [editingId,      setEditingId]      = useState(null);
  const [turnNo,         setTurnNo]         = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [message,        setMessage]        = useState({ text: '', type: '' });
  const [accounts,       setAccounts]       = useState([]);
  const [stocks,         setStocks]         = useState([]);
  const [filterAccount, setFilterAccount] = useState('');
  const [showUnsold,    setShowUnsold]    = useState(true);   // 미매도 체크박스
  const [showSold,      setShowSold]      = useState(false);  // 매도완료 체크박스
  const [priceMap,       setPriceMap]       = useState({});  // 실시간 배지 표시용
  const [chartPopup,     setChartPopup]     = useState(null);
  const [alertPopup,     setAlertPopup]     = useState(null);
  const [sortConfig,     setSortConfig]     = useState({ key: null, dir: 'asc' });
  const [stockNameWidth, setStockNameWidth] = useState(200);
  const [focusBuyId,     setFocusBuyId]     = useState(null);  // 다른 화면(메모 클릭)에서 넘어온 포커스 대상
  const [highlightBuyId, setHighlightBuyId] = useState(null);  // 포커스 강조 표시 (일시적)

  const dragStartX      = useRef(null);
  const dragStartW      = useRef(null);
  const priceAppliedRef = useRef(false);  // 무한루프 방지: 시세 적용 여부 추적
  const rowRefs         = useRef({});     // id → <tr> DOM (포커스 스크롤용)

  const computedBuyAmount =
    form.buy_price !== '' && form.quantity !== ''
      ? Math.round(parseFloat(form.buy_price) * parseFloat(form.quantity) * 100) / 100
      : '';

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // 탭이 활성화될 때마다 계좌/종목 콤보 재로드
  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(setAccounts).catch(() => {});
    fetchStocks({ is_active: true }).then(setStocks).catch(() => {});
  }, [isActive]);

  const loadBuyList = useCallback(async () => {
    priceAppliedRef.current = false;
    if (!showUnsold && !showSold) { setBuyList([]); return; }
    try {
      const params = {};
      if (filterAccount)          params.account_name = filterAccount;
      if (showUnsold && !showSold) params.is_sold = false;
      if (!showUnsold && showSold) params.is_sold = true;
      // 둘 다 체크: is_sold 파라미터 없음 (전체 조회)
      const data = await fetchBuyList(params);
      setBuyList(data);
    } catch {
      showMessage('목록 조회에 실패했습니다.', 'error');
    }
  }, [filterAccount, showUnsold, showSold]);

  useEffect(() => { loadBuyList(); }, [loadBuyList]);

  // 건별수익률현황 메모 클릭 등, 다른 화면에서 매수건 포커스 요청 구독
  // (멀티탭 구조라 비활성 상태에서도 항상 마운트되어 있어 언제든 수신 가능)
  useEffect(() => {
    const unsubscribe = subscribeTabNav(({ tab, buyId }) => {
      if (tab !== 'buy' || !buyId) return;
      setFilterAccount('');
      setShowUnsold(true);
      setShowSold(true);
      setFocusBuyId(buyId);
    });
    return unsubscribe;
  }, []);

  // focusBuyId 처리: 목록에 나타나면 (미매도면 수정폼 오픈 + 스크롤/강조, 매도완료면 스크롤/강조만)
  useEffect(() => {
    if (!focusBuyId) return;
    const item = buyList.find(i => i.id === focusBuyId);
    if (!item) return;
    if (!item.is_sold) handleRowClick(item);
    setHighlightBuyId(focusBuyId);
    setFocusBuyId(null);
    const el = rowRefs.current[focusBuyId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightBuyId(null), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyList, focusBuyId]);

  // 미매도 종목 실시간 시세 조회 → buyList 직접 업데이트
  // priceAppliedRef로 setBuyList 후 재실행 방지 (무한루프 차단)
  useEffect(() => {
    if (priceAppliedRef.current) return;
    const unsoldTickers = [...new Set(buyList.filter(i => !i.is_sold).map(i => i.ticker))];
    if (unsoldTickers.length === 0) return;
    fetchRealtimePrices(unsoldTickers).then(res => {
      console.log('시세조회 후 데이터:', res.data);
      const map = {};
      for (const p of res.data) map[p.ticker] = p;
      priceAppliedRef.current = true;  // setBuyList 전에 설정 (동기)
      setPriceMap(map);
      setBuyList(prev => prev.map(item => {
        if (item.is_sold) return item;
        const p = map[item.ticker];
        if (!p || p.current_price == null || !item.buy_price) return item;
        const rate = Math.round((p.current_price - item.buy_price) / item.buy_price * 10000) / 100;
        return {
          ...item,
          current_price:       p.current_price,
          current_profit_rate: rate,
          prev_diff:           p.prev_diff  ?? item.prev_diff,
          diff_rate:           p.diff_rate  ?? item.diff_rate,
        };
      }));
    }).catch(() => {});
  }, [buyList]);

  // 신규 모드에서 계좌+종목 선택 시 차수 자동배정
  useEffect(() => {
    if (editingId !== null || !form.account_name || !form.ticker) {
      if (editingId === null) setTurnNo(null);
      return;
    }
    fetchNextTurnNo(form.account_name, form.ticker)
      .then(setTurnNo)
      .catch(() => setTurnNo(null));
  }, [form.account_name, form.ticker, editingId]);

  // 수익률에 따른 색상 반환
  const getProfitColor = (rate) => {
    if (rate === null || rate === undefined || rate === 0) return 'inherit';
    return rate > 0 ? '#d32f2f' : '#1565c0';
  };

  // 정렬된 표시 목록 (buyList에 이미 current_profit_rate 포함)
  const displayList = useMemo(() => {
    if (!sortConfig.key) return buyList;
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    return [...buyList].sort((a, b) => {
      if (sortConfig.key === 'current_profit_rate') {
        const av = a.current_profit_rate ?? null;
        const bv = b.current_profit_rate ?? null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * dir;
      }
      const av = String(a[sortConfig.key] ?? '');
      const bv = String(b[sortConfig.key] ?? '');
      return av.localeCompare(bv) * dir;
    });
  }, [buyList, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const handleSearch = () => {
    setSortConfig({ key: null, dir: 'asc' });
    loadBuyList();
  };

  // 종목명 컬럼 드래그 리사이즈
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartW.current = stockNameWidth;
    const onMove = (ev) => {
      const delta = ev.clientX - dragStartX.current;
      setStockNameWidth(Math.min(400, Math.max(150, dragStartW.current + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleRowClick = (item) => {
    if (item.is_sold) return;
    setEditingId(item.id);
    setTurnNo(item.turn_no);
    setForm({
      account_name:       item.account_name,
      ticker:             item.ticker,
      buy_date:           item.buy_date || '',
      buy_price:          item.buy_price,
      quantity:           item.quantity,
      target_profit_rate: item.target_profit_rate ?? '',
      memo:               item.memo || '',
      exchange_rate:      item.exchange_rate ?? '',
    });
  };

  const handleNew = () => {
    setEditingId(null);
    setTurnNo(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const next = { ...form, [name]: value };
    if (name === 'ticker' && editingId === null) {
      const stock = stocks.find((s) => s.ticker === value);
      next.target_profit_rate = (stock && stock.target_profit_rate != null)
        ? stock.target_profit_rate : '';
    }
    setForm(next);

    // 매수일 입력/변경 시 환율 자동조회 (휴장일이면 직전 영업일 값) — 값이 이미 있어도 항상 재조회해서 덮어씀
    if (name === 'buy_date' && value) {
      fetchExchangeRate(value)
        .then(res => {
          if (res?.rate != null) {
            setForm(prev => ({ ...prev, exchange_rate: res.rate }));
          }
        })
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!form.account_name) { showMessage('계좌를 선택해주세요.', 'error');     return; }
    if (!form.ticker)       { showMessage('종목을 선택해주세요.', 'error');     return; }
    if (!form.buy_price)    { showMessage('매수단가를 입력해주세요.', 'error'); return; }
    if (!form.quantity)     { showMessage('수량을 입력해주세요.', 'error');     return; }

    setLoading(true);
    try {
      if (editingId === null) {
        const res = await createBuy({
          account_name:       form.account_name,
          ticker:             form.ticker,
          buy_date:           form.buy_date || null,
          buy_price:          parseFloat(form.buy_price),
          quantity:           parseFloat(form.quantity),
          target_profit_rate: form.target_profit_rate !== '' ? parseFloat(form.target_profit_rate) : null,
          memo:               form.memo || null,
          exchange_rate:      form.exchange_rate !== '' ? parseFloat(form.exchange_rate) : null,
        });
        showMessage(res.message || '매수가 등록되었습니다.');
      } else {
        await updateBuy(editingId, {
          buy_date:           form.buy_date || null,
          buy_price:          parseFloat(form.buy_price),
          quantity:           parseFloat(form.quantity),
          target_profit_rate: form.target_profit_rate !== '' ? parseFloat(form.target_profit_rate) : null,
          memo:               form.memo || null,
          exchange_rate:      form.exchange_rate !== '' ? parseFloat(form.exchange_rate) : null,
        });
        showMessage('매수 이력이 수정되었습니다.');
      }
      await loadBuyList();
      handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, item, e) => {
    e.stopPropagation();
    const label = `${item.account_name} / ${item.ticker} / ${item.turn_no}차`;
    if (!window.confirm(`"${label}" 매수건을 삭제하시겠습니까?`)) return;
    setLoading(true);
    try {
      await deleteBuy(id);
      showMessage('매수 이력이 삭제되었습니다.');
      await loadBuyList();
      if (editingId === id) handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const sortArrow = (key) =>
    sortConfig.key === key ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const toolbar = (
    <div className="buy-toolbar">
      <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)}>
        <option value="">전체</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.account_name}>{a.account_name}</option>
        ))}
      </select>
      <label className="buy-chk-label">
        <input type="checkbox" checked={showUnsold} onChange={(e) => setShowUnsold(e.target.checked)} />
        미매도
      </label>
      <label className="buy-chk-label">
        <input type="checkbox" checked={showSold} onChange={(e) => setShowSold(e.target.checked)} />
        매도완료
      </label>
      <button className="btn btn-secondary" onClick={handleSearch}>조회</button>
      <button className="btn btn-primary" onClick={handleNew}>+ 신규</button>
    </div>
  );

  return (
  <>
    <PageLayout title="매수 등록" toolbar={toolbar} message={message}>
      <div className="buy-body">

        {/* ── 좌측: 매수 목록 ── */}
        <div className="buy-list-panel">
          <div className="grid-wrapper">
            <table className="grid-table buy-grid">
              <thead>
                <tr>
                  <th
                    style={{ width: 90, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('account_name')}
                  >
                    계좌{sortArrow('account_name')}
                  </th>
                  <th
                    style={{ width: stockNameWidth, position: 'relative', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('stock_name')}
                  >
                    종목명{sortArrow('stock_name')}
                    <span
                      className="col-resize-handle"
                      onMouseDown={handleResizeMouseDown}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th
                    className="col-center"
                    style={{ width: 70, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('ticker')}
                  >
                    티커{sortArrow('ticker')}
                  </th>
                  <th className="col-center" style={{ width: 40 }}>차수</th>
                  <th
                    className="col-center"
                    style={{ width: 90, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('buy_date')}
                  >
                    매수일{sortArrow('buy_date')}
                  </th>
                  <th className="col-right"  style={{ width: 90 }}>단가</th>
                  <th className="col-right"  style={{ width: 70 }}>환율</th>
                  <th className="col-right"  style={{ width: 68 }}>수량</th>
                  <th className="col-right"  style={{ width: 100 }}>매수금액</th>
                  <th className="col-center" style={{ width: 66 }}>목표수익률</th>
                  <th className="col-right" style={{ width: 90 }}>현재가</th>
                  <th className="col-right" style={{ width: 110 }}>당일등락</th>
                  <th
                    className="col-center"
                    style={{ width: 66, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('current_profit_rate')}
                  >
                    현재수익률{sortArrow('current_profit_rate')}
                    {Object.keys(priceMap).length > 0 && (
                      <span style={{ fontSize: 9, color: '#0a8a00', marginLeft: 3 }}>실시간</span>
                    )}
                  </th>
                  <th className="col-center" style={{ width: 52 }}>매도</th>
                  <th className="col-center" style={{ width: 36 }}>🔔</th>
                  <th>메모</th>
                  <th className="col-center" style={{ width: 50 }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {displayList.length === 0 ? (
                  <tr><td colSpan={17} className="grid-empty">데이터가 없습니다.</td></tr>
                ) : (
                  displayList.map((item) => (
                    <tr
                      key={item.id}
                      ref={el => { if (el) rowRefs.current[item.id] = el; }}
                      onClick={() => handleRowClick(item)}
                      className={[
                        editingId === item.id ? 'selected' : '',
                        item.is_sold ? 'row-sold' : '',
                        highlightBuyId === item.id ? 'row-focus-highlight' : '',
                      ].join(' ')}
                    >
                      <td>{item.account_name}</td>
                      <td className="col-ellipsis">
                        <span
                          className="stock-chart-link"
                          onClick={() => setChartPopup({
                            ticker: item.ticker,
                            stockName: item.stock_name || item.ticker,
                            accountName: item.account_name,
                            currentPrice: item.current_price || null,
                            diffRate: item.diff_rate || null,
                            turns: item.buy_price > 0 ? [{ turn_no: item.turn_no || 1, buy_date: item.buy_date || null, buy_price: item.buy_price, quantity: item.quantity, profit_rate: item.current_profit_rate || null }] : [],
                          })}
                        >
                          {item.stock_name || '-'}
                        </span>
                      </td>
                      <td className="col-center">{item.ticker}</td>
                      <td className="col-center">{item.turn_no}</td>
                      <td className="col-center">{item.buy_date || ''}</td>
                      <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.buy_price)}</td>
                      <td className="col-right">{fNum(item.exchange_rate)}</td>
                      <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fQty(item.quantity)}</td>
                      <td className="col-right" style={!item.buy_date ? { color: PENDING_COLOR } : undefined}>{fNum(item.buy_amount)}</td>
                      <td className="col-center">
                        {item.target_profit_rate != null ? `${item.target_profit_rate}%` : ''}
                      </td>
                      <td className="col-right">
                        {!item.is_sold ? fNum(item.current_price) : ''}
                      </td>
                      <td className="col-right" style={{ color: !item.is_sold ? diffColor(item.diff_rate) : undefined }}>
                        {!item.is_sold ? fDiff(item.prev_diff, item.diff_rate) : ''}
                      </td>
                      <td className="col-center" style={{ color: getProfitColor(item.current_profit_rate), fontWeight: item.current_profit_rate ? 600 : 'normal' }}>
                        {!item.is_sold && item.current_profit_rate != null
                          ? `${parseFloat(item.current_profit_rate).toFixed(2)}%`
                          : ''}
                      </td>
                      <td className="col-center">
                        {item.is_sold
                          ? <span className="badge-sold">매도</span>
                          : <span className="badge-hold">보유</span>}
                      </td>
                      <td className="col-center">
                        {!item.is_sold && (
                          <AlertButton
                            hasAlert={item.alert_type_id_up != null || item.alert_type_id_down != null}
                            alertEnabled={!!item.alert_enabled}
                            alertTypeNameUp={item.alert_type_name_up}
                            alertTypeNameDown={item.alert_type_name_down}
                            onClick={() => setAlertPopup({ buyId: item.id, ticker: item.ticker, stockName: item.stock_name, accountName: item.account_name, turnNo: item.turn_no })}
                          />
                        )}
                      </td>
                      <td className="col-ellipsis">{item.memo || ''}</td>
                      <td className="col-center">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => handleDelete(item.id, item, e)}
                          disabled={loading || item.is_sold}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 우측: 등록·수정 폼 ── */}
        <div className="buy-form-panel">
          <h3 className="buy-form-title">{editingId === null ? '신규 등록' : '매수 수정'}</h3>

          <div className="sf-row">
            <label>계좌 <span className="required">*</span></label>
            <select
              name="account_name"
              value={form.account_name}
              onChange={handleFormChange}
              disabled={editingId !== null}
            >
              <option value="">선택</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.account_name}>{a.account_name}</option>
              ))}
            </select>
          </div>

          <div className="sf-row">
            <label>종목 <span className="required">*</span></label>
            <select
              name="ticker"
              value={form.ticker}
              onChange={handleFormChange}
              disabled={editingId !== null}
            >
              <option value="">선택</option>
              {stocks.map((s) => (
                <option key={s.id} value={s.ticker}>{s.stock_name} ({s.ticker})</option>
              ))}
            </select>
          </div>

          <div className="sf-row">
            <label>차수</label>
            <input
              type="text"
              value={turnNo != null ? `${turnNo}차` : '-'}
              readOnly
              className="input-readonly"
            />
          </div>

          <div className="sf-row">
            <label>매수일</label>
            <input
              type="date"
              name="buy_date"
              value={form.buy_date}
              onChange={handleFormChange}
            />
          </div>

          <div className="sf-row">
            <label>
              환율
              <span className="sf-help" title="매수일 기준 원달러 환율이 자동으로 채워집니다. 직접 수정할 수 있습니다.">?</span>
            </label>
            <input
              type="number"
              name="exchange_rate"
              value={form.exchange_rate}
              onChange={handleFormChange}
              min={0}
              step="any"
              placeholder="매수일 입력 시 자동조회"
            />
          </div>

          <div className="sf-row">
            <label>매수단가 <span className="required">*</span></label>
            <input
              type="number"
              name="buy_price"
              value={form.buy_price}
              onChange={handleFormChange}
              min={0}
              step="any"
              placeholder="0"
            />
          </div>

          <div className="sf-row">
            <label>수량 <span className="required">*</span></label>
            <input
              type="number"
              name="quantity"
              value={form.quantity}
              onChange={handleFormChange}
              min={0}
              step="any"
              placeholder="0"
            />
          </div>

          <div className="sf-row">
            <label>매수금액</label>
            <input
              type="text"
              value={computedBuyAmount !== '' ? fNum(computedBuyAmount) : ''}
              readOnly
              className="input-readonly"
              placeholder="자동계산"
            />
          </div>

          <div className="sf-row">
            <label>목표수익률</label>
            <div className="sf-input-suffix">
              <input
                type="number"
                name="target_profit_rate"
                value={form.target_profit_rate}
                onChange={handleFormChange}
                min={0}
                max={999}
                step={0.01}
                placeholder="예) 5.00"
              />
              <span className="sf-suffix">%</span>
            </div>
          </div>

          <div className="sf-row sf-row-memo">
            <label>메모</label>
            <textarea
              name="memo"
              value={form.memo}
              onChange={handleFormChange}
              maxLength={1000}
              rows={3}
              placeholder="메모 (선택)"
            />
          </div>

          <div className="sf-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </button>
            <button className="btn btn-secondary" onClick={handleNew} disabled={loading}>
              취소
            </button>
          </div>
        </div>

      </div>
    </PageLayout>
    {chartPopup && (
      <StockChartPopup
        ticker={chartPopup.ticker}
        stockName={chartPopup.stockName}
        accountName={chartPopup.accountName}
        currentPrice={chartPopup.currentPrice}
        diffRate={chartPopup.diffRate}
        turns={chartPopup.turns || []}
        onClose={() => setChartPopup(null)}
      />
    )}
    {alertPopup && (
      <AlertQuickPopup
        buyId={alertPopup.buyId}
        ticker={alertPopup.ticker}
        stockName={alertPopup.stockName}
        accountName={alertPopup.accountName}
        turnNo={alertPopup.turnNo}
        onClose={() => setAlertPopup(null)}
        onSaved={loadBuyList}
      />
    )}
  </>
  );
};

export default BuyPage;
